/**
 * ClaimQueueChips Component
 *
 * Horizontal row of smart queue filter chips with badge counts.
 * Calls the queue-counts endpoint independently of the claims list.
 */

import React from 'react';
import { Box, Chip, Skeleton, Stack } from '@mui/material';

import { QUEUE_CHIP_CONFIG } from '../../constants/statusOptions';
import { useClaimQueueCounts } from '../../store';

interface ClaimQueueChipsProps {
  activeQueue?: string;
  onQueueSelect: (status: string | undefined) => void;
  filters?: {
    patientId?: string;
    providerIds?: string;
    insurancePayerId?: string;
    dateFrom?: string;
    dateTo?: string;
    includeSuperseded?: boolean;
  };
}

const QUEUE_KEYS = ['needs_attention', 'ready_to_submit', 'blocked', 'submitted', 'denied', 'paid', 'imported'] as const;

export const ClaimQueueChips: React.FC<ClaimQueueChipsProps> = ({
  activeQueue,
  onQueueSelect,
  filters,
}) => {
  const { counts, isLoading } = useClaimQueueCounts(filters);

  if (isLoading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" width={90} height={28} sx={{ borderRadius: 999 }} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (!counts) return null;

  const handleClick = (queueKey: string) => {
    // Toggle: if clicking active queue, clear the filter
    if (activeQueue === queueKey) {
      onQueueSelect(undefined);
    } else {
      onQueueSelect(queueKey);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {QUEUE_KEYS.map(key => {
          const config = QUEUE_CHIP_CONFIG[key];
          const count = counts[key as keyof typeof counts] ?? 0;
          const isActive = activeQueue === key;

          return (
            <Chip
              key={key}
              label={`${config.label} (${count})`}
              color={config.color}
              variant={isActive ? 'filled' : 'outlined'}
              size="small"
              onClick={() => handleClick(key)}
              aria-label={`${config.label}: ${count} claims`}
              sx={{
                cursor: 'pointer',
                fontWeight: isActive ? 600 : 400,
                '& .MuiChip-label': { fontSize: '0.8rem' },
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
};
